/**
 * Backup-Status — liegen die Sicherungen wirklich dort, wo sie liegen sollen?
 *
 * Ein Backup, das niemand ansieht, ist eine Vermutung. Diese Seite beantwortet
 * die drei Fragen, die im Ernstfall zaehlen: In welchem Verzeichnis liegen die
 * Sicherungen? Wie alt ist die juengste? Fehlt dazwischen etwas?
 *
 * Datenquelle ist `GET /api/backup/inventar`. Die Zahlen darin erhebt nicht das
 * Backend, sondern ein Skript auf dem Server (`deploy-v2/backup-inventar.py`,
 * stuendlich per Cron), das die Hetzner Storage Box ausliest. Der Container hat
 * bewusst keinen Zugang dorthin — deshalb ist der angezeigte Stand immer der
 * des letzten Sammellaufs und nicht der dieser Sekunde. Genau deshalb steht das
 * Erhebungsdatum sichtbar auf der Seite und nicht im Kleingedruckten.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  CircleStackIcon,
  ClockIcon,
  CloudIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import { StatCard, StatGroup } from '../components/StatCard';

interface Luecke {
  von: string;
  bis: string;
  stunden: number;
  fehlende_laeufe: number;
}

interface Reihe {
  schluessel: string;
  bezeichnung: string;
  zweck: string;
  pfad: string;
  intervall_stunden: number;
  retention: number;
  dumps: number;
  configs: number;
  ohne_config: number;
  aeltester: string | null;
  neuester: string | null;
  neuester_bytes: number | null;
  gesamt_bytes: number;
  alter_stunden: number | null;
  luecken: Luecke[];
  fehlende_laeufe: number;
  fehler: string | null;
}

interface Inventar {
  erhoben_am: string;
  veraltet: boolean;
  stand_von: string | null;
  storage_box: { host: string; basis: string; erreichbar: boolean; fehler: string | null };
  reihen: Reihe[];
  mailcow: { pfad: string; anzahl: number; neuester: string | null; alter_stunden: number | null; fehler: string | null } | null;
  dateien: { pfad: string; ordner: string[]; remote_bytes: number | null; lokal_bytes: number | null; fehler: string | null } | null;
  lokale_sicherungen: { name: string; bytes: number; geaendert: string }[];
  monitor: { state: string | null; letzte_meldung: string | null; letzter_fehler: string | null };
  zeitplan: { takt: string; befehl: string }[];
  platte: { gesamt_bytes: number; belegt_bytes: number; frei_bytes: number; belegt_prozent: number };
}

interface Antwort {
  vorhanden: boolean;
  hinweis?: string;
  datei_geaendert?: string;
  inventar: Inventar | null;
}

const bytes = (b: number | null | undefined): string => {
  if (b == null) return '—';
  const einheiten = ['B', 'KB', 'MB', 'GB', 'TB'];
  let wert = b;
  let i = 0;
  while (wert >= 1024 && i < einheiten.length - 1) {
    wert /= 1024;
    i += 1;
  }
  return `${wert.toFixed(wert >= 100 || i === 0 ? 0 : 1)} ${einheiten[i]}`;
};

const zeitpunkt = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/**
 * "vor 3 Stunden" statt "3.2" — im Zweifel interessiert nicht die Zahl,
 * sondern ob die Sicherung von heute ist.
 */
const alter = (stunden: number | null | undefined): string => {
  if (stunden == null) return '—';
  if (stunden < 1) return 'vor unter 1 Std.';
  if (stunden < 24) return `vor ${Math.round(stunden)} Std.`;
  const tage = Math.round(stunden / 24);
  return `vor ${tage} ${tage === 1 ? 'Tag' : 'Tagen'}`;
};

/** Zeitraum, den eine Reihe tatsächlich abdeckt — die eigentliche Rückfallreichweite. */
const abdeckung = (r: Reihe): string => {
  if (!r.aeltester || !r.neuester) return '—';
  const spanne = (new Date(r.neuester).getTime() - new Date(r.aeltester).getTime()) / 36e5;
  if (spanne < 48) return `${Math.round(spanne)} Stunden`;
  const tage = Math.round(spanne / 24);
  if (tage < 21) return `${tage} Tage`;
  return `${Math.round(tage / 7)} Wochen`;
};

const AdminBackupStatus: React.FC = () => {
  const [daten, setDaten] = useState<Antwort | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const holen = useCallback(async () => {
    setLaden(true);
    setFehler(null);
    try {
      const antwort = await apiClient.get('/api/backup/inventar');
      setDaten(antwort.data?.data ?? null);
    } catch (e: any) {
      setFehler(e?.response?.data?.message || 'Backup-Status konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    holen();
  }, [holen]);

  const inv = daten?.inventar ?? null;
  const reihen = inv?.reihen ?? [];
  const dumpsGesamt = reihen.reduce((s, r) => s + r.dumps, 0);
  const lueckenGesamt = reihen.reduce((s, r) => s + r.fehlende_laeufe, 0);
  // Die 2h-Reihe ist die Referenz für "wie aktuell ist die Sicherung" — sie
  // läuft am häufigsten, jede andere Reihe wäre hier zwangsläufig älter.
  const juengste = reihen.find((r) => r.schluessel === '2h') ?? reihen[0];

  const kopf = (
    <header className="app-page-header mb-6 hidden border-b-[3px] border-lime-400 bg-white dark:bg-gray-800 sm:block">
      <div className="flex items-center justify-between gap-3 p-4 md:px-8 md:py-6">
        <div className="flex items-center gap-3">
          <ArchiveBoxIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
          <div>
            <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">Backup-Status</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Wo die Sicherungen liegen, wie alt sie sind und ob etwas fehlt
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={holen}
          disabled={laden}
          aria-label="Neu laden"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <ArrowPathIcon className={`h-4 w-4 ${laden ? 'animate-spin' : ''}`} aria-hidden="true" />
        </button>
      </div>
    </header>
  );

  if (laden && !daten) {
    return (
      <div className="mx-auto max-w-7xl">
        {kopf}
        <div className="card animate-pulse space-y-3">
          <div className="h-4 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-2/3 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      </div>
    );
  }

  if (fehler) {
    return (
      <div className="mx-auto max-w-7xl">
        {kopf}
        <div className="card border-error-300 dark:border-error-700">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">{fehler}</p>
              <button
                type="button"
                onClick={holen}
                className="mt-3 rounded-lg border border-gray-300 px-3 py-1.5 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Erneut versuchen
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Der Sammler läuft noch nicht: ein Zustand, kein Fehler. Deshalb neutral
  // gehalten — eine rote Karte hier liest sich wie "Backups kaputt".
  if (!daten?.vorhanden || !inv) {
    return (
      <div className="mx-auto max-w-7xl">
        {kopf}
        <div className="card">
          <div className="flex items-start gap-3">
            <ServerStackIcon className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p className="font-semibold text-gray-900 dark:text-white">Noch keine Erhebung vorhanden</p>
              <p className="mt-1">{daten?.hinweis || 'Es liegt keine Inventardatei vor.'}</p>
              <p className="mt-3">
                Das Inventar schreibt <code className="rounded bg-gray-100 px-1.5 py-0.5 text-[12px] dark:bg-gray-700">backup-inventar.py</code> auf
                dem Server nach <code className="rounded bg-gray-100 px-1.5 py-0.5 text-[12px] dark:bg-gray-700">backups/_inventar.json</code>.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const box = inv.storage_box;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {kopf}

      {/* Ist die Storage Box gerade erreichbar? Steht vor allen Zahlen, weil sie
          deren Belastbarkeit bestimmt. */}
      {!box.erreichbar && (
        <div className="rounded-xl border border-warning-300 bg-warning-50 p-4 dark:border-warning-700 dark:bg-warning-900/20">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-warning-600 dark:text-warning-400" />
            <div className="text-sm">
              <p className="font-semibold text-gray-900 dark:text-white">
                Storage Box beim letzten Sammellauf nicht erreichbar
              </p>
              <p className="mt-1 text-gray-600 dark:text-gray-400">
                Die folgenden Zahlen stammen vom {zeitpunkt(inv.stand_von)} und können veraltet sein.
                {box.fehler ? ` Meldung: ${box.fehler}` : ''}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <StatGroup columns={4}>
          <StatCard
            icon={ClockIcon}
            label="Jüngste Sicherung"
            value={alter(juengste?.alter_stunden)}
            sub={zeitpunkt(juengste?.neuester)}
            tone={juengste && juengste.alter_stunden != null && juengste.alter_stunden > 3 ? 'warning' : 'success'}
            valueTone={juengste && juengste.alter_stunden != null && juengste.alter_stunden > 3 ? 'warning' : 'neutral'}
          />
          <StatCard
            icon={CircleStackIcon}
            label="Datenbank-Sicherungen"
            value={dumpsGesamt}
            sub={reihen.map((r) => `${r.dumps}× ${r.bezeichnung.toLowerCase()}`).join(' · ')}
            tone="info"
          />
          <StatCard
            icon={lueckenGesamt > 0 ? ExclamationTriangleIcon : CheckCircleIcon}
            label="Fehlende Läufe"
            value={lueckenGesamt}
            sub={lueckenGesamt === 0 ? 'Ketten lückenlos' : 'siehe Reihen unten'}
            tone={lueckenGesamt > 0 ? 'error' : 'success'}
            valueTone={lueckenGesamt > 0 ? 'error' : 'success'}
          />
          <StatCard
            icon={CloudIcon}
            label="Belegt auf Storage Box"
            value={bytes(
              reihen.reduce((s, r) => s + r.gesamt_bytes, 0) + (inv.dateien?.remote_bytes ?? 0)
            )}
            sub={`${box.host}:${box.basis}`}
            tone="primary"
          />
        </StatGroup>
      </div>

      {/* Je Reihe eine Karte: Verzeichnis, Takt, Reichweite, Lücken. */}
      {reihen.map((r) => {
        const gestoert = !!r.fehler || r.fehlende_laeufe > 0;
        return (
          <div key={r.schluessel} className="card">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                    gestoert
                      ? 'bg-warning-50 dark:bg-warning-900/20'
                      : 'bg-primary-50 dark:bg-primary-900/20'
                  }`}
                >
                  <CircleStackIcon
                    className={`h-5 w-5 ${
                      gestoert
                        ? 'text-warning-600 dark:text-warning-400'
                        : 'text-primary-600 dark:text-primary-400'
                    }`}
                  />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">{r.bezeichnung}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{r.zweck}</p>
                  <p className="mt-1 break-all font-mono text-[12px] text-gray-500 dark:text-gray-400">
                    {box.host}:{r.pfad}
                  </p>
                </div>
              </div>
              <span
                className={`rounded-lg px-2.5 py-1 text-[12px] font-semibold ${
                  r.fehler
                    ? 'bg-error-50 text-error-700 dark:bg-error-900/20 dark:text-error-300'
                    : r.fehlende_laeufe > 0
                    ? 'bg-warning-50 text-warning-700 dark:bg-warning-900/20 dark:text-warning-300'
                    : 'bg-success-50 text-success-700 dark:bg-success-900/20 dark:text-success-300'
                }`}
              >
                {r.fehler ? 'Nicht lesbar' : r.fehlende_laeufe > 0 ? `${r.fehlende_laeufe} Lauf/Läufe fehlen` : 'Lückenlos'}
              </span>
            </div>

            {r.fehler ? (
              <p className="text-sm text-error-600 dark:text-error-400">{r.fehler}</p>
            ) : (
              <>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Takt</dt>
                    <dd className="mt-0.5 text-sm text-gray-900 dark:text-white">
                      alle {r.intervall_stunden < 24 ? `${r.intervall_stunden} Std.` : `${r.intervall_stunden / 24} Tage`}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Bestand</dt>
                    <dd className="mt-0.5 text-sm tabular-nums text-gray-900 dark:text-white">
                      {r.dumps} von max. {r.retention}
                      {r.ohne_config > 0 && (
                        <span className="ml-1 text-warning-600 dark:text-warning-400">
                          ({r.ohne_config} ohne Config)
                        </span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Reicht zurück</dt>
                    <dd className="mt-0.5 text-sm text-gray-900 dark:text-white">{abdeckung(r)}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Belegt</dt>
                    <dd className="mt-0.5 text-sm tabular-nums text-gray-900 dark:text-white">
                      {bytes(r.gesamt_bytes)}
                    </dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Ältester Stand</dt>
                    <dd className="mt-0.5 text-sm tabular-nums text-gray-900 dark:text-white">{zeitpunkt(r.aeltester)}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Jüngster Stand</dt>
                    <dd className="mt-0.5 text-sm tabular-nums text-gray-900 dark:text-white">
                      {zeitpunkt(r.neuester)}
                      <span className="ml-2 text-gray-500 dark:text-gray-400">
                        {bytes(r.neuester_bytes)} · {alter(r.alter_stunden)}
                      </span>
                    </dd>
                  </div>
                </dl>

                {r.luecken.length > 0 && (
                  <div className="mt-4 rounded-lg border border-warning-200 bg-warning-50 p-3 dark:border-warning-800 dark:bg-warning-900/20">
                    <p className="mb-2 text-[12px] font-bold uppercase tracking-wider text-warning-700 dark:text-warning-300">
                      Lücken in der Kette
                    </p>
                    <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      {r.luecken.map((l) => (
                        <li key={`${l.von}-${l.bis}`} className="tabular-nums">
                          {zeitpunkt(l.von)} → {zeitpunkt(l.bis)}
                          <span className="ml-2 text-gray-500 dark:text-gray-400">
                            ({l.stunden} Std., {l.fehlende_laeufe} Lauf/Läufe)
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* Dateien und Mailcow nebeneinander — beides Bestandsangaben ohne Kette. */}
      <div className="grid gap-6 lg:grid-cols-2">
        {inv.dateien && (
          <div className="card">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-info-50 dark:bg-info-900/20">
                <FolderIcon className="h-5 w-5 text-info-600 dark:text-info-400" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Dateien</h2>
                <p className="break-all font-mono text-[12px] text-gray-500 dark:text-gray-400">
                  {box.host}:{inv.dateien.pfad}
                </p>
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Auf Storage Box</dt>
                <dd className="mt-0.5 text-sm tabular-nums text-gray-900 dark:text-white">{bytes(inv.dateien.remote_bytes)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Auf dem Server</dt>
                <dd className="mt-0.5 text-sm tabular-nums text-gray-900 dark:text-white">{bytes(inv.dateien.lokal_bytes)}</dd>
              </div>
            </dl>
            {/*
              Remote ist regelmäßig größer als lokal — gesichert wird ohne
              --delete, die Storage Box behält also auch gelöschte Dateien.
              Ohne diesen Satz sieht die Differenz nach einem Fehler aus.
            */}
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Die Sicherung läuft additiv (ohne <code className="font-mono">--delete</code>). Auf der Storage Box
              liegen deshalb auch Dateien, die auf dem Server gelöscht wurden.
            </p>
            {inv.dateien.ordner.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {inv.dateien.ordner.map((o) => (
                  <span
                    key={o}
                    className="rounded-md border border-gray-200 px-2 py-1 font-mono text-[12px] text-gray-600 dark:border-gray-600 dark:text-gray-300"
                  >
                    {o}
                  </span>
                ))}
              </div>
            )}
            {inv.dateien.fehler && (
              <p className="mt-3 text-sm text-error-600 dark:text-error-400">{inv.dateien.fehler}</p>
            )}
          </div>
        )}

        {inv.mailcow && (
          <div className="card">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary-900/20">
                <ServerStackIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Mailserver</h2>
                <p className="break-all font-mono text-[12px] text-gray-500 dark:text-gray-400">
                  {box.host}:{inv.mailcow.pfad}
                </p>
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Sicherungen</dt>
                <dd className="mt-0.5 text-sm tabular-nums text-gray-900 dark:text-white">{inv.mailcow.anzahl}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Jüngste</dt>
                <dd className="mt-0.5 text-sm text-gray-900 dark:text-white">{alter(inv.mailcow.alter_stunden)}</dd>
              </div>
            </dl>
            {inv.mailcow.neuester && (
              <p className="mt-3 break-all font-mono text-[12px] text-gray-500 dark:text-gray-400">
                {inv.mailcow.neuester}
              </p>
            )}
            {inv.mailcow.fehler && (
              <p className="mt-3 text-sm text-error-600 dark:text-error-400">{inv.mailcow.fehler}</p>
            )}
          </div>
        )}
      </div>

      {/* Von Hand angelegte Dumps auf dem Server — nicht ausgelagert, deshalb
          getrennt von allem oben. */}
      {inv.lokale_sicherungen.length > 0 && (
        <div className="card">
          <div className="mb-4 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-700/40">
              <DocumentDuplicateIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Sicherungen auf dem Server</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Von Hand angelegt, etwa vor Migrationen. Diese liegen <strong>nur</strong> auf dem Server.
              </p>
            </div>
          </div>
          <div className="-mx-2 overflow-x-auto">
            <table className="w-full min-w-[420px] text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left dark:border-gray-700">
                  <th className="px-2 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Datei</th>
                  <th className="px-2 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Größe</th>
                  <th className="px-2 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Erstellt</th>
                </tr>
              </thead>
              <tbody>
                {inv.lokale_sicherungen.map((s) => (
                  <tr key={s.name} className="border-b border-gray-100 last:border-0 dark:border-gray-700/60">
                    <td className="break-all px-2 py-2 font-mono text-[12px] text-gray-900 dark:text-white">{s.name}</td>
                    <td className="whitespace-nowrap px-2 py-2 tabular-nums text-gray-600 dark:text-gray-300">{bytes(s.bytes)}</td>
                    <td className="whitespace-nowrap px-2 py-2 tabular-nums text-gray-600 dark:text-gray-300">{zeitpunkt(s.geaendert)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {inv.zeitplan.length > 0 && (
          <div className="card">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-info-50 dark:bg-info-900/20">
                <CalendarDaysIcon className="h-5 w-5 text-info-600 dark:text-info-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Zeitplan</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Was der Server wann ausführt</p>
              </div>
            </div>
            <ul className="space-y-2">
              {inv.zeitplan.map((j) => (
                <li key={j.befehl + j.takt} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
                  <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[12px] text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                    {j.takt}
                  </code>
                  <span className="break-all text-gray-600 dark:text-gray-400">{j.befehl}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="card">
          <div className="mb-4 flex items-start gap-3">
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                inv.platte.belegt_prozent >= 90 ? 'bg-error-50 dark:bg-error-900/20' : 'bg-gray-100 dark:bg-gray-700/40'
              }`}
            >
              <ServerStackIcon
                className={`h-5 w-5 ${
                  inv.platte.belegt_prozent >= 90
                    ? 'text-error-600 dark:text-error-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Serverplatte</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Ohne freien Platz kann kein Dump geschrieben werden
              </p>
            </div>
          </div>
          <div className="mb-2 flex items-baseline justify-between text-sm">
            <span className="tabular-nums text-gray-900 dark:text-white">
              {bytes(inv.platte.belegt_bytes)} von {bytes(inv.platte.gesamt_bytes)}
            </span>
            <span
              className={`font-semibold tabular-nums ${
                inv.platte.belegt_prozent >= 90
                  ? 'text-error-600 dark:text-error-400'
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {inv.platte.belegt_prozent} %
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className={`h-full rounded-full ${
                inv.platte.belegt_prozent >= 90 ? 'bg-error-500' : 'bg-primary-600'
              }`}
              style={{ width: `${Math.min(100, inv.platte.belegt_prozent)}%` }}
            />
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Noch frei: <span className="tabular-nums">{bytes(inv.platte.frei_bytes)}</span>
          </p>
          {inv.monitor.state && (
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              Überwachung:{' '}
              <span
                className={
                  inv.monitor.state === 'OK'
                    ? 'font-semibold text-success-600 dark:text-success-400'
                    : 'font-semibold text-error-600 dark:text-error-400'
                }
              >
                {inv.monitor.state}
              </span>
              {inv.monitor.letzter_fehler && (
                <span className="mt-1 block break-all font-mono text-[11px] text-gray-500 dark:text-gray-400">
                  Letzter Fehler: {inv.monitor.letzter_fehler}
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      <p className="pb-2 text-center text-xs text-gray-500 dark:text-gray-400">
        Stand der Erhebung: {zeitpunkt(inv.erhoben_am)}
        {daten.datei_geaendert ? ` · Datei geschrieben: ${zeitpunkt(daten.datei_geaendert)}` : ''}
      </p>
    </div>
  );
};

export default AdminBackupStatus;
